import { Product, RecurringPaymentTag, OneOffPaymentTag, UpgradeTag } from '../api/paymentsService';
import { Feature } from '../models';

export enum GroupTypesB2C {
  PREMIUM = 'premium',
  ESSENTIAL = 'essential',
  FREE_TRIAL = 'free_trial',
}

export enum Period {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export enum Status {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export enum Currency {
  DOLLAR = 'usd',
  EURO = 'eur',
  POND = 'gbp',
  AUD = 'aud',
}

export enum Options {
  No = 'No',
  Active = 'Active',
  Expired = 'Expired',
  Premium = 'Premium',
  Essential = 'Essential',
}

export const currencySymbolList = {
  usd: '$',
  eur: '€ ',
  gbp: '£',
  aud: '$',
};

const essentialFeatureList: Feature[] = [
  { key: 'feature_one', checked: true },
  { key: 'feature_two', checked: true },
  { key: 'feature_three', checked: true },
  { key: 'feature_four', checked: false },
  { key: 'feature_five', checked: false },
  { key: 'feature_six', checked: false },
];

const premiumFeatureList: Feature[] = [
  { key: 'feature_one', checked: true },
  { key: 'feature_two', checked: true },
  { key: 'feature_three', checked: true },
  { key: 'feature_four', checked: true },
  { key: 'feature_five', checked: true },
  { key: 'feature_six', checked: true },
];

export function getProductId(products: Product[], packageName: GroupTypesB2C, period: Period, oneOffPayment?: boolean) {
  const product = getProduct(products, packageName, period, oneOffPayment);

  if (!product) {
    return '';
  }

  return product?.id;
}

export function createPricePlans(packageName: GroupTypesB2C, currency: Currency, prices: any) {
  const productPrice = prices[`${packageName}_year`][currency].amount;
  let price = Number(productPrice) / 100;
  price /= 12;
  return `${currencySymbolList[currency]}${price.toFixed(2)}`;
}

export function createPrice(products: Product[], packageName: GroupTypesB2C, period: Period, currency: Currency, monthly = false) {
  const product = getProduct(products, packageName, period);

  if (!product) {
    return '-';
  }

  let price = product.prices[currency].amount / 100;

  if (monthly && period === Period.YEARLY) {
    price /= 12;
  }
  return `${currencySymbolList[currency]}${price.toFixed(2)}`;
}

export function parseGroupType(groupType: string) {
  switch (groupType) {
    case GroupTypesB2C.PREMIUM:
      return GroupTypesB2C.PREMIUM;
    case GroupTypesB2C.ESSENTIAL:
      return GroupTypesB2C.ESSENTIAL;
    case GroupTypesB2C.FREE_TRIAL:
      return GroupTypesB2C.FREE_TRIAL;
    default:
      return GroupTypesB2C.ESSENTIAL;
  }
}

export function parsePeriod(period: string) {
  switch (period) {
    case Period.MONTHLY:
      return Period.MONTHLY;
    case Period.YEARLY:
      return Period.YEARLY;
    default:
      return Period.YEARLY;
  }
}

export function getProductFeatures(groupType: GroupTypesB2C) {
  if (groupType === GroupTypesB2C.PREMIUM) {
    return premiumFeatureList;
  }

  if (groupType === GroupTypesB2C.ESSENTIAL) {
    return essentialFeatureList;
  }
  return [];
}

function getProduct(products: Product[], packageName: GroupTypesB2C, period: Period, oneOffPayment?: boolean | undefined): Product | undefined {
  if (packageName === GroupTypesB2C.PREMIUM) {
    if (period === Period.MONTHLY) {
      return products.find(
        element => element.tags.indexOf(!oneOffPayment ? RecurringPaymentTag.B2C_PREMIUM_MONTH : OneOffPaymentTag.B2C_PREMIUM_ONE_OFF_MONTH) >= 0
      );
    }
    if (period === Period.YEARLY) {
      return products.find(
        element => element.tags.indexOf(!oneOffPayment ? RecurringPaymentTag.B2C_PREMIUM_YEAR : OneOffPaymentTag.B2C_PREMIUM_ONE_OFF_YEAR) >= 0
      );
    }
  }

  if (packageName === GroupTypesB2C.ESSENTIAL) {
    if (period === Period.MONTHLY) {
      return products.find(element => element.tags.indexOf(!oneOffPayment ? RecurringPaymentTag.B2C_ESSENTIAL_MONTH :
        OneOffPaymentTag.B2C_ESSENTIAL_ONE_OFF_MONTH) >= 0);
    }
    if (period === Period.YEARLY) {
      return products.find(element => element.tags.indexOf(!oneOffPayment ? RecurringPaymentTag.B2C_ESSENTIAL_YEAR :
        OneOffPaymentTag.B2C_ESSENTIAL_ONE_OFF_YEAR) >= 0);
    }
  }
  return undefined;
}

export async function getUpgradePriceAndId(products: Product[], numberOfCurrentMonth: number, calculatedUpgrade: boolean, currency: Currency) {
  let product;
  if (!calculatedUpgrade) {
    product = products.find(element => element.tags.indexOf(UpgradeTag.B2C_PREMIUM_UPGRADE_MONTH) >= 0);
  } else {
    product = products.find(
      element => element.tags.indexOf(`${UpgradeTag.B2C_PREMIUM_UPGRADE_YEAR_MONTH_1.slice(0, -1)}${numberOfCurrentMonth + 1}`) >= 0
    );
  }

  if (!product) {
    throw new Error('No product');
  }
  const upgradePrice = Number(product.prices[currency].amount) / 100;
  return { upgradePrice, upgradeProductId: product.id };
}

export function getCurrencyByCountry(currencies: any, country: string) {
  let currency: Currency = Currency.DOLLAR;
  if (currencies.default) {
    currency = currencies.default.toLowerCase();
  }

  if (currencies.countries && Object.keys(currencies.countries).includes(country)) {
    currency = currencies.countries[country].toLowerCase();
  }
  return currency;
}
